/* eslint-disable @typescript-eslint/naming-convention */
import { type ComponentId } from './api';
import type { OmitKeys, ReplaceProperties } from '../../common/types/util';

export type ComponentLifeModelResponse = {
  dealerId: number;
  companyName: string;
  companyId: number;
  siteName: string;
  siteId: number;
  siteUuid: string;
  assetId: number;
  assetExternalIntId: number | null;
  assetGroupName: string;
  assetTypeName: string;
  manufacturerName: string;
  modelNumber: string;
  serialNumber: string;
  assetName: string;
  coverageType: string | null;
  assetUrl: string;
  assetUuid: string;
  siteUrl: string;
  isAssetModule: boolean;
  companyUrl: string;
  companyUuid: string;
  componentName: string;
  componentId: ComponentId;
  componentPartNumber: string;
  componentQuantity: number | null;
  componentVendorId: number | null;
  componentVendorName: string;
  remainingLifePercent: number | null;
  lastChangeDate: string | null;
  nextChangeDate: string | null;
  formattedRemainingLifePercent: string;
  formattedNextChangeDate: string;
  overrideExpectedLifeHoursAtFullLoad: number | null;
  expectedLifeHoursAtFullLoad: number;
  scheduleStartDate: string | null;
  scheduleIntervalMonths: number | null;
  componentLifeType: ComponentLifeType;
  daysRemaining: number | null;
  componentInitialized: boolean;
  firstChangeDue: string | null;
  fullLoadHoursSinceLastReset: number | null;
  formattedLastChangeDate: string;
  formattedDaysRemainingStatus: string;
};

export type ComponentLifeType = 'Hours' | 'Schedule' | 'DynamicLinked';

export const EComponentLifeType = {
  Hours: 'Hours',
  DynamicLinked: 'DynamicLinked',
  Schedule: 'Schedule'
} as const satisfies Record<ComponentLifeType, ComponentLifeType>;

export function componentLifeTypeIsHoursBases(type: ComponentLifeType) {
  return type === EComponentLifeType.Hours || type === EComponentLifeType.DynamicLinked;
}

export function componentLifeTypeGetDescription(type: ComponentLifeType): string {
  switch (type) {
    case EComponentLifeType.Hours:
      return 'Track the life based on the hours of runtime';
    case EComponentLifeType.Schedule:
      return 'Set a fixed replacement schedule';
    case EComponentLifeType.DynamicLinked:
      return 'Track the life based on the hours of runtime. Automatically resets the component when a replacement activity is performed';
  }
}

export type ComponentLifeModel = ReplaceProperties<
  ComponentLifeModelResponse,
  {
    lastChangeDate: Date | null;
    nextChangeDate: Date | null;
    firstChangeDue: Date | null;
  }
>;

export interface AssetTypeComponent {
  id: number;
  name: string;
  assetTypeId: number;
  expectedLifeHoursAtFullLoad: number;
}

export interface PartVendor {
  id: number;
  name: string;
}

export type ComponentLifeResponse = {
  componentLifeEnabled?: boolean;
  components: ComponentLifeModelResponse[];
  partVendors: PartVendor[];
  name?: string;
  id: number;
};

export interface UpsertComponentDto {
  assetTypeComponentId: number | null;
  componentId: number;
  quantity: number | null;
  partNumber: string | null;
  vendorId: number | null;
  newVendorName: string | null;
  updateVendor: boolean;
  assetId: number;
  newComponent: boolean;
  overrideFullLoadHours: number | null;
  scheduleStartDate: string | null;
  scheduleIntervalMonths: number | null;
  componentLifeType: ComponentLifeType;
}

export interface ComponentReplacementResponse {
  assetComponentId: number;
  date: string;
  hours: number;
}

export type ComponentReplacement = OmitKeys<ComponentReplacementResponse, 'date'> & { date: Date };

export type ComponentReplacementAddRequest = {
  assetComponentId: number;
  date: Date;
};

export type AssetComponentResetResponse = {
  assetComponentId: number;
  date: string;
};

export type AssetComponentReset = {
  assetComponentId: number;
  date: Date;
};
